import { useEffect } from "react";
import { useQuery, useSubscription, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import {
  QUERY_TPMS_FRAME_DATA,
  QUERY_TPMS_CONFIG,
  QUERY_TPMS_RECEIVER_STATE,
  QUERY_MOST_RECENT_FRAMES,
  MUTATE_TPMS_CONFIG,
  SUBSCRIBE_TPMS_CONFIG,
  SUBSCRIBE_TPMS_BATCH_FRAMES,
  SUBSCRIBE_TPMS_RECEIVER_STATE,
  MUTATE_SET_SELECTED_TYRES,
  QUERY_USING_AKS_WEATHER,
  SUBSCRIBE_USING_AKS_WEATHER_UPDATE,
  MUTATE_USING_AKS_WEATHER,
} from "../queries/TPMSQueries";

import {
  setLoadingState,
  setHistoricalDataLoaded,
  setTPMSConfig,
  setTPMSData,
  updateTPMSData,
  setReceiverState,
  setUsingAKSWeather,
} from "../../../state/tpmsReducer";

import { useDispatch } from "react-redux";
import { RootState } from "../../../state";

const tEndDate = new Date();
const tStartDate = new Date();
tStartDate.setHours(tStartDate.getHours() - 3);
const tStart = tStartDate.getTime();
const tEnd = tEndDate.getTime();

export function QueryUsingAKSWeather() {
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery(QUERY_USING_AKS_WEATHER);
  if (error) {
    console.log(error);
  }
  useEffect(() => {
    if (data) {
      dispatch(setUsingAKSWeather(data.getUsingAKSWeather));
    }
  }, [dispatch, data, loading, error]);
}

export function QueryTPMSData() {
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery(QUERY_TPMS_FRAME_DATA, {
    variables: { tStart: tStart, tEnd: tEnd },
  });
  if (error) {
    console.log(error);
  }
  useEffect(() => {
    if (data) {
      dispatch(setTPMSData(data.getTPMSFrameData));
      dispatch(setHistoricalDataLoaded(true));
    }
  }, [dispatch, data, loading, error]);
}

export function QueryMostRecentFrames() {
  const dispatch = useDispatch();
  const historicalDataLoaded = useSelector(
    (state: RootState) => state.tpms.historicalDataLoaded,
  );
  const { data, error } = useQuery(QUERY_MOST_RECENT_FRAMES, {
    variables: { tStart: tStart, tEnd: tEnd },
    skip: !historicalDataLoaded, // Wait for QueryTPMSData to complete first
  });
  if (error) {
    console.log(error);
  }
  useEffect(() => {
    if (data) {
      const { frameType0, frameType1 } = data.getMostRecentFrames;
      for (const frame of frameType0 || []) {
        dispatch(updateTPMSData({ frameType: 0, frame }));
      }
      for (const frame of frameType1 || []) {
        dispatch(updateTPMSData({ frameType: 1, frame }));
      }
      dispatch(setLoadingState(false)); // Both queries done
    }
  }, [dispatch, data]);
}

export function QueryTPMSConfig() {
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery(QUERY_TPMS_CONFIG);
  if (error) {
    console.log(error);
  }
  useEffect(() => {
    if (data) {
      console.log("LATEST_CONFIG", data.getTPMSConfig);
      dispatch(setTPMSConfig(data.getTPMSConfig));
    }
  }, [dispatch, data, loading, error]);
}

export function QueryTPMSReceiverState() {
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery(QUERY_TPMS_RECEIVER_STATE);
  if (error) {
    console.log(error);
  }
  useEffect(() => {
    if (data) {
      console.log("RECEIVER_STATE", data.getTPMSReceiverState);
      dispatch(setReceiverState(data.getTPMSReceiverState));
    }
  }, [dispatch, data, loading, error]);
}

export function SubscribeUsingAKSWeatherUpdate() {
  const dispatch = useDispatch();
  const { error } = useSubscription(SUBSCRIBE_USING_AKS_WEATHER_UPDATE, {
    onData: (x) => {
      const usingAKSWeather = x.data.data.newUsingAKSWeatherUpdate;
      dispatch(setUsingAKSWeather(usingAKSWeather));
    },
  });
  if (error) {
    console.log(error);
  }
}

export function SubscribeTPMSConfig() {
  const dispatch = useDispatch();
  const { error } = useSubscription(SUBSCRIBE_TPMS_CONFIG, {
    onData: (x) => {
      const config: Record<string, unknown> = x.data.data.newTPMSConfig;
      for (const key of Object.keys(config)) {
        if (config[key] === null) {
          delete config[key];
        }
      }
      dispatch(setTPMSConfig(config));
    },
  });
  if (error) {
    console.log(error);
  }
}

export function MutateUsingAKSWeather() {
  const [setUsingAKSWeather] = useMutation(MUTATE_USING_AKS_WEATHER);
  return setUsingAKSWeather;
}

export function MutateTPMSConfig() {
  const [updateTPMSConfig] = useMutation(MUTATE_TPMS_CONFIG);
  return updateTPMSConfig;
}

export function SubscribeTPMSBatchFrames() {
  const dispatch = useDispatch();
  const { error } = useSubscription(SUBSCRIBE_TPMS_BATCH_FRAMES, {
    onData: (x) => {
      const frameType0 = x.data.data.newTPMSBatchFrames.frameType0;
      const frameType1 = x.data.data.newTPMSBatchFrames.frameType1;
      for (const frame of frameType0) {
        dispatch(updateTPMSData({ frameType: 0, frame: frame }));
      }
      for (const frame of frameType1) {
        dispatch(updateTPMSData({ frameType: 1, frame: frame }));
      }
    },
  });
  if (error) {
    console.log(error);
  }
}

export function SubscribeTPMSReceiverState() {
  const dispatch = useDispatch();
  const { error } = useSubscription(SUBSCRIBE_TPMS_RECEIVER_STATE, {
    onData: (x) => {
      const state = x.data.data.newTPMSReceiverState;
      dispatch(setReceiverState(state));
    },
  });
  if (error) {
    console.log(error);
  }
}

export function MutateSetSelectedTyres() {
  const [setSelectedTyres] = useMutation(MUTATE_SET_SELECTED_TYRES);
  return setSelectedTyres;
}
